import type { CharacterId } from './characters';
import { DEFAULT_INITIALS, normaliseInitials } from './initials';

/**
 * The high score board, kept on the player's own machine in `localStorage`.
 *
 * localStorage rather than cookies on purpose: cookies are capped around 4KB
 * and are attached to every single request to the site, which this board has
 * no reason to be.
 *
 * This is the local board, and now doubles as the offline cache for the global
 * one: the authoritative board lives behind the Lambda described in
 * docs/global-leaderboard.md.
 */
export interface HighscoreEntry {
  /** Three arcade-style initials, entered after a run. */
  initials: string;
  character: CharacterId;
  score: number;
  survivedMs: number;
  /** Epoch milliseconds, so the board can show recency later. */
  recordedAt: number;
}

// Initials live in their own module so the leaderboard's Lambda can use the
// rule without importing this one, which touches localStorage. Re-exported
// here because callers reasonably expect to find them alongside the board.
export { DEFAULT_INITIALS, INITIALS_LENGTH, normaliseInitials } from './initials';

const STORAGE_KEY = 'koodooracers.highscores.v2';
const MIGRATED_KEY = 'koodooracers.highscores.migrated.v1';
const MAX_ENTRIES = 10;

/** True when a score is good enough to earn a place on the board. */
export function qualifies(entries: readonly HighscoreEntry[], score: number): boolean {
  if (score <= 0) {
    return false;
  }
  if (entries.length < MAX_ENTRIES) {
    return true;
  }
  return entries.some((entry) => score > entry.score);
}

export function parseHighscores(raw: string | null): HighscoreEntry[] {
  if (!raw) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .filter(isEntry)
    .map((entry) => ({
      ...entry,
      // Boards written before initials existed still load, as 'AAA'.
      initials: normaliseInitials(
        typeof entry.initials === 'string' ? entry.initials : DEFAULT_INITIALS,
      ),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_ENTRIES);
}

function isEntry(value: unknown): value is HighscoreEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const entry = value as Partial<HighscoreEntry>;
  return (
    typeof entry.character === 'string' &&
    typeof entry.score === 'number' &&
    Number.isFinite(entry.score) &&
    entry.score >= 0
  );
}

/** Inserts a result and keeps only the best few, highest first. */
export function addHighscore(
  entries: readonly HighscoreEntry[],
  entry: HighscoreEntry,
): HighscoreEntry[] {
  return [...entries, entry].sort((a, b) => b.score - a.score).slice(0, MAX_ENTRIES);
}

export function bestScore(entries: readonly HighscoreEntry[]): number {
  return entries.reduce((best, entry) => Math.max(best, entry.score), 0);
}

export function loadHighscores(storage = safeStorage()): HighscoreEntry[] {
  if (!storage) {
    return [];
  }
  try {
    return parseHighscores(storage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

export function saveHighscores(
  entries: readonly HighscoreEntry[],
  storage = safeStorage(),
): void {
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Private browsing or a full quota — scores simply do not persist.
  }
}

/**
 * Whether the scores already in this browser have been pushed to the global
 * board. Boards predate the global leaderboard, so the first run after it ships
 * carries them up rather than stranding them — see docs/global-leaderboard.md.
 */
export function hasMigrated(storage = safeStorage()): boolean {
  try {
    return storage?.getItem(MIGRATED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markMigrated(storage = safeStorage()): void {
  try {
    storage?.setItem(MIGRATED_KEY, 'true');
  } catch {
    // Same as saving scores: nothing to do if the browser refuses to remember.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return globalThis.window?.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}
